// ============= AUDIO GENERATION (Web Audio API) =============

export const createAudioContext = () => {
  try {
    return new (window.AudioContext || window.webkitAudioContext)();
  } catch (e) {
    console.warn('Failed to create AudioContext:', e);
    return null;
  }
};

// Helper to ensure AudioContext is resumed (browsers require user gesture)
export const ensureAudioResumed = async (audioCtx) => {
  if (!audioCtx) return false;
  try {
    if (audioCtx.state === 'suspended') {
      await audioCtx.resume();
    }
    return audioCtx.state === 'running';
  } catch (e) {
    console.warn('Failed to resume AudioContext:', e);
    return false;
  }
};

// Rain sound - continuous white noise filtered
export const createRainSound = (audioCtx) => {
  if (!audioCtx || audioCtx.state !== 'running') return null;
  
  try {
    const bufferSize = Math.floor(audioCtx.sampleRate * 4);
    const buffer = audioCtx.createBuffer(1, bufferSize, audioCtx.sampleRate);
    const data = buffer.getChannelData(0);
    
    for (let i = 0; i < bufferSize; i++) {
      data[i] = (Math.random() * 2 - 1) * 0.15;
    }
    
    const source = audioCtx.createBufferSource();
    const filter = audioCtx.createBiquadFilter();
    const gainNode = audioCtx.createGain();
    
    filter.type = 'lowpass';
    filter.frequency.value = 3000;
    
    source.buffer = buffer;
    source.loop = true;
    source.connect(filter);
    filter.connect(gainNode);
    gainNode.connect(audioCtx.destination);
    
    gainNode.gain.setValueAtTime(0, audioCtx.currentTime);
    gainNode.gain.linearRampToValueAtTime(0.25, audioCtx.currentTime + 1);
    
    source.start();
    
    return { source, gainNode };
  } catch (e) {
    console.warn('Rain sound error:', e);
    return null;
  }
};

// Thunder sound
export const playThunder = (audioCtx) => {
  const duration = 2 + Math.random() * 2;
  const bufferSize = audioCtx.sampleRate * duration;
  const buffer = audioCtx.createBuffer(1, bufferSize, audioCtx.sampleRate);
  const data = buffer.getChannelData(0);
  
  // Create rumbling thunder
  for (let i = 0; i < bufferSize; i++) {
    const t = i / audioCtx.sampleRate;
    const envelope = Math.exp(-t * 1.5) * (0.5 + Math.random() * 0.5);
    data[i] = (Math.random() * 2 - 1) * envelope * 0.4;
  }
  
  const source = audioCtx.createBufferSource();
  const filter = audioCtx.createBiquadFilter();
  const gainNode = audioCtx.createGain();
  
  filter.type = 'lowpass';
  filter.frequency.value = 150 + Math.random() * 100;
  
  source.buffer = buffer;
  source.connect(filter);
  filter.connect(gainNode);
  gainNode.connect(audioCtx.destination);
  
  gainNode.gain.setValueAtTime(0.6, audioCtx.currentTime);
  
  source.start();
};

// Wind sound for snow/clouds - Very gentle, filtered, like breath through wool
export const createSnowWindSound = (audioCtx) => {
  if (!audioCtx || audioCtx.state !== 'running') return null;
  
  try {
    const bufferSize = Math.floor(audioCtx.sampleRate * 6);
    const buffer = audioCtx.createBuffer(1, bufferSize, audioCtx.sampleRate);
    const data = buffer.getChannelData(0);
    
    // Very gentle wind with slow modulation
    for (let i = 0; i < bufferSize; i++) {
      const t = i / audioCtx.sampleRate;
      // Slow breathing modulation
      const mod = 0.3 + 0.2 * Math.sin(t * 0.3) + 0.1 * Math.sin(t * 0.7);
      data[i] = (Math.random() * 2 - 1) * mod * 0.06;
    }
    
    const source = audioCtx.createBufferSource();
    const filter = audioCtx.createBiquadFilter();
    const filter2 = audioCtx.createBiquadFilter();
    const gainNode = audioCtx.createGain();
    
    // Very aggressive low-pass for muffled sound
    filter.type = 'lowpass';
    filter.frequency.value = 400;
    filter.Q.value = 0.5;
    
    filter2.type = 'lowpass';
    filter2.frequency.value = 600;
    
    source.buffer = buffer;
    source.loop = true;
    source.connect(filter);
    filter.connect(filter2);
    filter2.connect(gainNode);
    gainNode.connect(audioCtx.destination);
    
    gainNode.gain.setValueAtTime(0, audioCtx.currentTime);
    gainNode.gain.linearRampToValueAtTime(0.15, audioCtx.currentTime + 2);
    
    source.start();
    
    return { source, gainNode };
  } catch (e) {
    console.warn('Snow wind error:', e);
    return null;
  }
};

// Very subtle wind for clouds - ethereal, dreamy
export const createCloudWindSound = (audioCtx) => {
  if (!audioCtx || audioCtx.state !== 'running') return null;
  
  try {
    const bufferSize = Math.floor(audioCtx.sampleRate * 8);
    const buffer = audioCtx.createBuffer(1, bufferSize, audioCtx.sampleRate);
    const data = buffer.getChannelData(0);
    
    // Ultra-soft wind with very slow modulation
    for (let i = 0; i < bufferSize; i++) {
      const t = i / audioCtx.sampleRate;
      const mod = 0.2 + 0.15 * Math.sin(t * 0.15) + 0.08 * Math.sin(t * 0.4);
      data[i] = (Math.random() * 2 - 1) * mod * 0.03;
    }
    
    const source = audioCtx.createBufferSource();
    const filter = audioCtx.createBiquadFilter();
    const gainNode = audioCtx.createGain();
    
    filter.type = 'lowpass';
    filter.frequency.value = 300;
    
    source.buffer = buffer;
    source.loop = true;
    source.connect(filter);
    filter.connect(gainNode);
    gainNode.connect(audioCtx.destination);
    
    gainNode.gain.setValueAtTime(0, audioCtx.currentTime);
    gainNode.gain.linearRampToValueAtTime(0.08, audioCtx.currentTime + 3);
    
    source.start();
    
    return { source, gainNode };
  } catch (e) {
    console.warn('Cloud wind error:', e);
    return null;
  }
};

// Dreamy ambient music for clouds - ethereal pads and gentle tones
export const createCloudMusic = (audioCtx) => {
  if (!audioCtx || audioCtx.state !== 'running') return null;
  
  try {
    const oscillators = [];
    const masterGain = audioCtx.createGain();
    masterGain.connect(audioCtx.destination);
    masterGain.gain.value = 0.08;
    
    // Create dreamy chord pad (C major 7th spread across octaves)
    const frequencies = [130.81, 164.81, 196.00, 246.94]; // C3, E3, G3, B3
    
    frequencies.forEach((freq, i) => {
      const osc = audioCtx.createOscillator();
      const oscGain = audioCtx.createGain();
      const filter = audioCtx.createBiquadFilter();
      
      osc.type = 'sine';
      osc.frequency.value = freq;
      osc.detune.value = (Math.random() - 0.5) * 10; // Slight detuning for warmth
      
      filter.type = 'lowpass';
      filter.frequency.value = 800;
      
      oscGain.gain.value = 0.25;
      
      osc.connect(filter);
      filter.connect(oscGain);
      oscGain.connect(masterGain);
      osc.start();
      
      oscillators.push({ osc, gain: oscGain });
    });
    
    // Add a higher octave shimmer
    const shimmerOsc = audioCtx.createOscillator();
    const shimmerGain = audioCtx.createGain();
    shimmerOsc.type = 'sine';
    shimmerOsc.frequency.value = 523.25; // C5
    shimmerGain.gain.value = 0.05;
    shimmerOsc.connect(shimmerGain);
    shimmerGain.connect(masterGain);
    shimmerOsc.start();
    oscillators.push({ osc: shimmerOsc, gain: shimmerGain });
    
    // Gentle LFO modulation on volume for breathing effect
    const lfoInterval = setInterval(() => {
      if (audioCtx.state !== 'running') return;
      const now = audioCtx.currentTime;
      masterGain.gain.linearRampToValueAtTime(0.06 + Math.random() * 0.04, now + 2);
    }, 3000);
    
    return { oscillators, masterGain, lfoInterval };
  } catch (e) {
    console.warn('Cloud music error:', e);
    return null;
  }
};

// Stop cloud music
export const stopCloudMusic = (musicRef, audioCtx) => {
  if (!musicRef) return;
  
  if (musicRef.lfoInterval) clearInterval(musicRef.lfoInterval);
  
  if (musicRef.oscillators) {
    musicRef.oscillators.forEach(({ osc }) => {
      try { osc.stop(); } catch { /* ignore */ }
    });
  }
};

// Rain sound for thunderstorm - heavier, more intense
export const createThunderstormRainSound = (audioCtx) => {
  if (!audioCtx || audioCtx.state !== 'running') return null;
  
  try {
    const bufferSize = Math.floor(audioCtx.sampleRate * 4);
    const buffer = audioCtx.createBuffer(1, bufferSize, audioCtx.sampleRate);
    const data = buffer.getChannelData(0);
    
    // Heavier rain with more variation
    for (let i = 0; i < bufferSize; i++) {
      const variation = 0.8 + Math.random() * 0.4;
      data[i] = (Math.random() * 2 - 1) * 0.2 * variation;
    }
    
    const source = audioCtx.createBufferSource();
    const filter = audioCtx.createBiquadFilter();
    const gainNode = audioCtx.createGain();
    
    filter.type = 'lowpass';
    filter.frequency.value = 4000;
    
    source.buffer = buffer;
    source.loop = true;
    source.connect(filter);
    filter.connect(gainNode);
    gainNode.connect(audioCtx.destination);
    
    gainNode.gain.setValueAtTime(0, audioCtx.currentTime);
    gainNode.gain.linearRampToValueAtTime(0.3, audioCtx.currentTime + 1);
    
    source.start();
    
    return { source, gainNode };
  } catch (e) {
    console.warn('Thunderstorm rain error:', e);
    return null;
  }
};

// Beach wave sound - gentle, rhythmic ocean
export const createBeachWaveSound = (audioCtx) => {
  if (!audioCtx || audioCtx.state !== 'running') return null;
  
  try {
    const bufferSize = Math.floor(audioCtx.sampleRate * 10);
    const buffer = audioCtx.createBuffer(1, bufferSize, audioCtx.sampleRate);
    const data = buffer.getChannelData(0);
    
    // Create wave-like pattern with slow modulation
    for (let i = 0; i < bufferSize; i++) {
      const t = i / audioCtx.sampleRate;
      // Wave envelope - slow rolling waves
      const waveEnv = 0.4 + 0.3 * Math.sin(t * 0.5) + 0.2 * Math.sin(t * 0.23);
      // Add some foam/splash variation
      const splash = Math.random() > 0.997 ? Math.random() * 0.3 : 0;
      data[i] = (Math.random() * 2 - 1) * (waveEnv * 0.12 + splash);
    }
    
    const source = audioCtx.createBufferSource();
    const filter = audioCtx.createBiquadFilter();
    const filter2 = audioCtx.createBiquadFilter();
    const gainNode = audioCtx.createGain();
    
    filter.type = 'lowpass';
    filter.frequency.value = 800;
    
    filter2.type = 'highpass';
    filter2.frequency.value = 100;
    
    source.buffer = buffer;
    source.loop = true;
    source.connect(filter);
    filter.connect(filter2);
    filter2.connect(gainNode);
    gainNode.connect(audioCtx.destination);
    
    gainNode.gain.setValueAtTime(0, audioCtx.currentTime);
    gainNode.gain.linearRampToValueAtTime(0.25, audioCtx.currentTime + 2);
    
    source.start();
    
    return { source, gainNode };
  } catch (e) {
    console.warn('Beach wave error:', e);
    return null;
  }
};

// Seagull call
export const playSeagullCall = async (audioCtx) => {
  if (!audioCtx || audioCtx.state !== 'running') return;
  
  try {
    const osc = audioCtx.createOscillator();
    const gainNode = audioCtx.createGain();
    
    osc.type = 'sine';
    osc.connect(gainNode);
    gainNode.connect(audioCtx.destination);
    
    const now = audioCtx.currentTime;
    const duration = 0.3 + Math.random() * 0.2;
    
    // Seagull-like frequency sweep
    const baseFreq = 800 + Math.random() * 400;
    osc.frequency.setValueAtTime(baseFreq, now);
    osc.frequency.linearRampToValueAtTime(baseFreq * 1.5, now + duration * 0.3);
    osc.frequency.linearRampToValueAtTime(baseFreq * 0.8, now + duration);
    
    gainNode.gain.setValueAtTime(0, now);
    gainNode.gain.linearRampToValueAtTime(0.08, now + 0.02);
    gainNode.gain.linearRampToValueAtTime(0.05, now + duration * 0.5);
    gainNode.gain.linearRampToValueAtTime(0, now + duration);
    
    osc.start(now);
    osc.stop(now + duration);
  } catch (e) {
    // Silently ignore seagull errors
  }
};

// Soothing thunder - dramatic but not jarring, with rumble
export const playSoothingThunder = async (audioCtx, intensity = 1.0) => {
  if (!audioCtx || audioCtx.state !== 'running') return;
  
  try {
    // Main thunder rumble
    const duration = 2 + Math.random() * 2.5;
    const bufferSize = Math.floor(audioCtx.sampleRate * duration);
    const buffer = audioCtx.createBuffer(1, bufferSize, audioCtx.sampleRate);
    const data = buffer.getChannelData(0);
    
    // Create multi-layered thunder with initial crack and rumble
    for (let i = 0; i < bufferSize; i++) {
      const t = i / audioCtx.sampleRate;
      
      // IMMEDIATE SHARP CRACK at start (first 0.1 seconds)
      let crackPart = 0;
      if (t < 0.08) {
        crackPart = (Math.random() * 2 - 1) * (1 - t/0.08) * 0.9;
      }
      
      // Rumble envelope - starts right after crack, sustains longer
      const rumbleDelay = 0.05;
      let envelope = 0;
      if (t > rumbleDelay) {
        const rumbleT = t - rumbleDelay;
        envelope = Math.exp(-rumbleT * 0.8) * (0.7 + Math.random() * 0.3);
      }
      
      // Add sub-bass rumble for depth
      const subBass = Math.sin(t * 30 + Math.random() * 0.2) * envelope * 0.3;
      
      // Combine crack and rumble
      data[i] = (crackPart + (Math.random() * 2 - 1) * envelope * 0.5 + subBass) * intensity;
    }
    
    const source = audioCtx.createBufferSource();
    const filter = audioCtx.createBiquadFilter();
    const filter2 = audioCtx.createBiquadFilter();
    const gainNode = audioCtx.createGain();
    
    // Allow more high frequencies through for the crack
    filter.type = 'lowpass';
    filter.frequency.value = 400 + Math.random() * 200;
    
    // Boost sub frequencies for rumble
    filter2.type = 'peaking';
    filter2.frequency.value = 60;
    filter2.gain.value = 6;
    
    source.buffer = buffer;
    source.connect(filter);
    filter.connect(filter2);
    filter2.connect(gainNode);
    gainNode.connect(audioCtx.destination);
    
    // VERY LOUD initial volume - increased for prominence
    gainNode.gain.setValueAtTime(1.0 * intensity, audioCtx.currentTime);
    gainNode.gain.linearRampToValueAtTime(0.7 * intensity, audioCtx.currentTime + 0.3);
    
    source.start();
    
    // Add a second, delayed rumble for depth
    setTimeout(() => {
      if (audioCtx.state !== 'running') return;
      try {
        const rumble2 = audioCtx.createBufferSource();
        const gain2 = audioCtx.createGain();
        const filter3 = audioCtx.createBiquadFilter();
        
        const rumbleDur = 1.5 + Math.random();
        const rumbleSize = Math.floor(audioCtx.sampleRate * rumbleDur);
        const rumbleBuffer = audioCtx.createBuffer(1, rumbleSize, audioCtx.sampleRate);
        const rumbleData = rumbleBuffer.getChannelData(0);
        
        for (let i = 0; i < rumbleSize; i++) {
          const t = i / audioCtx.sampleRate;
          const env = Math.exp(-t * 1.2) * 0.5;
          rumbleData[i] = (Math.random() * 2 - 1) * env;
        }
        
        filter3.type = 'lowpass';
        filter3.frequency.value = 200;
        
        rumble2.buffer = rumbleBuffer;
        rumble2.connect(filter3);
        filter3.connect(gain2);
        gain2.connect(audioCtx.destination);
        gain2.gain.setValueAtTime(0.5 * intensity, audioCtx.currentTime);
        
        rumble2.start();
      } catch { /* ignore */ }
    }, 150 + Math.random() * 200);
    
  } catch (e) {
    console.warn('Thunder error:', e);
  }
};

// Ambient tone for breathing
export const playAmbientTone = (audioCtx, frequency = 220, duration = 2, volume = 0.1) => {
  if (!audioCtx || audioCtx.state !== 'running') return;
  
  try {
    const osc = audioCtx.createOscillator();
    const gainNode = audioCtx.createGain();
    
    osc.type = 'sine';
    osc.frequency.value = frequency;
    osc.connect(gainNode);
    gainNode.connect(audioCtx.destination);
    
    gainNode.gain.setValueAtTime(0, audioCtx.currentTime);
    gainNode.gain.linearRampToValueAtTime(volume, audioCtx.currentTime + 0.3);
    gainNode.gain.linearRampToValueAtTime(0, audioCtx.currentTime + duration);
    
    osc.start();
    osc.stop(audioCtx.currentTime + duration);
  } catch { /* ignore */ }
};

// Soft click for sorting
export const playSoftClick = (audioCtx) => {
  if (!audioCtx || audioCtx.state !== 'running') return;
  
  try {
    const osc = audioCtx.createOscillator();
    const gainNode = audioCtx.createGain();
    
    osc.type = 'sine';
    osc.frequency.value = 800;
    osc.connect(gainNode);
    gainNode.connect(audioCtx.destination);
    
    gainNode.gain.setValueAtTime(0.15, audioCtx.currentTime);
    gainNode.gain.exponentialRampToValueAtTime(0.001, audioCtx.currentTime + 0.1);
    
    osc.start();
    osc.stop(audioCtx.currentTime + 0.1);
  } catch { /* ignore */ }
};

// Pour sound for tea ceremony
export const playPourSound = (audioCtx, duration = 1) => {
  if (!audioCtx || audioCtx.state !== 'running') return;
  
  try {
    const bufferSize = Math.floor(audioCtx.sampleRate * duration);
    const buffer = audioCtx.createBuffer(1, bufferSize, audioCtx.sampleRate);
    const data = buffer.getChannelData(0);
    
    for (let i = 0; i < bufferSize; i++) {
      const t = i / audioCtx.sampleRate;
      const envelope = Math.sin(t / duration * Math.PI) * 0.3;
      data[i] = (Math.random() * 2 - 1) * envelope;
    }
    
    const source = audioCtx.createBufferSource();
    const filter = audioCtx.createBiquadFilter();
    const gainNode = audioCtx.createGain();
    
    filter.type = 'bandpass';
    filter.frequency.value = 2000;
    filter.Q.value = 2;
    
    source.buffer = buffer;
    source.connect(filter);
    filter.connect(gainNode);
    gainNode.connect(audioCtx.destination);
    
    gainNode.gain.value = 0.2;
    source.start();
  } catch { /* ignore */ }
};

// ============= TEA CEREMONY MUSIC =============

const PENTATONIC_SCALE = {
  C4: 261.63, D4: 293.66, E4: 329.63, G4: 392.00, A4: 440.00,
};

const SCALE_NOTES = Object.values(PENTATONIC_SCALE);

// Create ambient pad for tea ceremony
const createAmbientPad = (audioCtx) => {
  if (!audioCtx || audioCtx.state !== 'running') return null;
  
  try {
    const oscillators = [];
    const masterGain = audioCtx.createGain();
    masterGain.connect(audioCtx.destination);
    masterGain.gain.value = 0.06;
    
    // Create 3 detuned oscillators for rich pad sound
    [0, 5, -5].forEach(detune => {
      const osc = audioCtx.createOscillator();
      const oscGain = audioCtx.createGain();
      
      osc.type = 'sine';
      osc.frequency.value = 130.81; // C3 - low drone
      osc.detune.value = detune;
      
      oscGain.gain.value = 0.3;
      
      osc.connect(oscGain);
      oscGain.connect(masterGain);
      osc.start();
      
      oscillators.push({ osc, gain: oscGain });
    });
    
    return { oscillators, masterGain };
  } catch (e) {
    console.warn('Ambient pad error:', e);
    return null;
  }
};

// Play a koto-like plucked note
const playKotoNote = (audioCtx, frequency, volume = 0.15) => {
  if (!audioCtx || audioCtx.state !== 'running') return;
  
  try {
    const osc = audioCtx.createOscillator();
    const osc2 = audioCtx.createOscillator();
    const gainNode = audioCtx.createGain();
    const filter = audioCtx.createBiquadFilter();
    
    // Main tone
    osc.type = 'triangle';
    osc.frequency.value = frequency;
    
    // Harmonic for richness
    osc2.type = 'sine';
    osc2.frequency.value = frequency * 2;
    
    filter.type = 'lowpass';
    filter.frequency.value = frequency * 4;
    filter.Q.value = 1;
    
    osc.connect(filter);
    osc2.connect(filter);
    filter.connect(gainNode);
    gainNode.connect(audioCtx.destination);
    
    const now = audioCtx.currentTime;
    
    // Pluck envelope - fast attack, slow decay
    gainNode.gain.setValueAtTime(0, now);
    gainNode.gain.linearRampToValueAtTime(volume, now + 0.01);
    gainNode.gain.exponentialRampToValueAtTime(volume * 0.3, now + 0.3);
    gainNode.gain.exponentialRampToValueAtTime(0.001, now + 2.5);
    
    // Filter sweep for more natural pluck
    filter.frequency.setValueAtTime(frequency * 6, now);
    filter.frequency.exponentialRampToValueAtTime(frequency * 2, now + 0.5);
    
    osc.start(now);
    osc2.start(now);
    osc.stop(now + 3);
    osc2.stop(now + 3);
  } catch { /* ignore */ }
};

// Play wind chime sound
const playWindChime = (audioCtx, volume = 0.1) => {
  if (!audioCtx || audioCtx.state !== 'running') return;
  
  try {
    const frequencies = [523.25, 659.25, 783.99, 1046.50]; // C5, E5, G5, C6
    const freq = frequencies[Math.floor(Math.random() * frequencies.length)];
    
    const osc = audioCtx.createOscillator();
    const gainNode = audioCtx.createGain();
    
    osc.type = 'sine';
    osc.frequency.value = freq;
    osc.connect(gainNode);
    gainNode.connect(audioCtx.destination);
    
    const now = audioCtx.currentTime;
    gainNode.gain.setValueAtTime(0, now);
    gainNode.gain.linearRampToValueAtTime(volume, now + 0.01);
    gainNode.gain.exponentialRampToValueAtTime(0.001, now + 2);
    
    osc.start(now);
    osc.stop(now + 2.5);
  } catch { /* ignore */ }
};

// Create and start tea ceremony music
export const createTeaCeremonyMusic = (audioCtx) => {
  if (!audioCtx || audioCtx.state !== 'running') return null;
  
  const pad = createAmbientPad(audioCtx);
  
  // Play random koto notes periodically
  const kotoInterval = setInterval(() => {
    if (audioCtx.state !== 'running') return;
    const note = SCALE_NOTES[Math.floor(Math.random() * SCALE_NOTES.length)];
    playKotoNote(audioCtx, note, 0.1 + Math.random() * 0.05);
    
    // Sometimes play a second note for harmony
    if (Math.random() > 0.6) {
      setTimeout(() => {
        const note2 = SCALE_NOTES[Math.floor(Math.random() * SCALE_NOTES.length)];
        playKotoNote(audioCtx, note2, 0.08);
      }, 200 + Math.random() * 300);
    }
  }, 2500 + Math.random() * 2000);
  
  // Occasional wind chime
  const chimeInterval = setInterval(() => {
    if (audioCtx.state !== 'running') return;
    if (Math.random() > 0.5) {
      playWindChime(audioCtx, 0.06 + Math.random() * 0.04);
    }
  }, 4000 + Math.random() * 3000);
  
  return { pad, kotoInterval, chimeInterval };
};

// Stop tea ceremony music
export const stopTeaCeremonyMusic = (musicRef, audioCtx) => {
  if (!musicRef) return;
  
  if (musicRef.kotoInterval) clearInterval(musicRef.kotoInterval);
  if (musicRef.chimeInterval) clearInterval(musicRef.chimeInterval);
  
  if (musicRef.pad && musicRef.pad.oscillators) {
    musicRef.pad.oscillators.forEach(({ osc }) => {
      try { osc.stop(); } catch { /* ignore */ }
    });
  }
};
